import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import apiClient from '/src/api/client';
import { Link, useNavigate } from 'react-router-dom';
import poslahLogo from '../../../assets/poslahlogo.png';
import '../../../css/auth-pages.css';

const MotionDiv = motion.div;
const MotionImg = motion.img;
const MotionButton = motion.button;

const SignIn = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

   useEffect(() => {
    if (window.socket) {
      window.socket.disconnect();
      window.socket = null;
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const trimmedUsername = username.trim();
      const trimmedPassword = password.trim();
      
      // Validate inputs
      if (!trimmedUsername || !trimmedPassword) {
        setError('Please enter both username and password');
        setIsLoading(false);
        return;
      }
      
      console.log('🔐 Attempting login...');
      
      // Use apiClient directly - the enhanced retry logic is built into the interceptors
      const { data } = await apiClient.post('/api/v1/auth/login', { 
        username: trimmedUsername,
        password: trimmedPassword
      });
      
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('userDetails', JSON.stringify(data.user));
        console.log('✅ Login successful, redirecting...');
        navigate('/welcome', { replace: true });
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (loginError) {
      console.error('❌ Login error:', loginError);
      
      // Enhanced error handling for production deployment issues
      if (loginError.response?.status === 401) {
        setError('Invalid username or password');
      } else if (loginError.response?.status === 404) {
        setError('User not found. Please check your credentials.');
      } else if (loginError.response?.status === 500) {
        setError('Server error. Please try again in a few moments.');
      } else if (loginError.response?.status === 502) {
        setError('Server is temporarily unavailable. Please try again.');
      } else if (loginError.response?.status === 503) {
        setError('Service temporarily unavailable. Please try again.');
      } else if (loginError.response?.status === 504) {
        setError('Server timeout. The server may be starting up. Please wait a moment and try again.');
      } else if (loginError.code === 'ECONNABORTED' || loginError.message?.includes('timeout')) {
        setError('Connection timeout. The server may be starting up or experiencing high traffic. Please try again.');
      } else if (loginError.response?.data?.code === 'CONNECTION_TIMEOUT') {
        setError('Database connection timeout. Please try again in a moment.');
      } else if (loginError.message?.includes('database')) {
        setError('Database connection issue. Please try again in a moment.');
      } else if (loginError.message?.includes('Network Error') || loginError.code === 'ERR_NETWORK') {
        setError('Network connection failed. Please check your internet connection and try again.');
      } else if (loginError.code === 'ERR_INTERNET_DISCONNECTED') {
        setError('No internet connection. Please check your network and try again.');
      } else if (loginError.message?.includes('CORS')) {
        setError('Configuration error. Please contact support if this persists.');
      } else if (loginError.message?.includes('SSL') || loginError.message?.includes('certificate')) {
        setError('Secure connection error. Please try again or contact support.');
      } else {
        // Enhanced fallback error message with more context
        const errorMessage = loginError.response?.data?.message || 
                            loginError.message || 
                            'Login failed. Please try again.';
        
        // Add context for production issues
        if (errorMessage.includes('connect') || errorMessage.includes('ECONNREFUSED')) {
          setError('Unable to connect to server. Please try again in a few moments.');
        } else if (errorMessage.includes('ENOTFOUND') || errorMessage.includes('DNS')) {
          setError('Server not found. Please check your connection and try again.');
        } else {
          setError(errorMessage);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="signin-page">
      {/* Animated Background */}
      <div className="animated-bg"></div>
      
      {/* Floating Elements */}
      <div className="floating-elements">
        <MotionDiv 
          className="floating-element"
          animate={{
            y: [-20, 20, -20],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <MotionDiv 
          className="floating-element"
          animate={{
            y: [20, -20, 20],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      <MotionDiv 
        className="signin-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <MotionDiv className="signin-content" variants={itemVariants}>
          {/* Logo Section */}
          <MotionDiv className="logo-section" variants={itemVariants}>
            <Link to="/">
              <MotionImg 
                src={poslahLogo} 
                alt="POSlah Logo"
                className="signin-logo"
                whileHover={{ scale: 1.05, rotate: 2 }}
                transition={{ duration: 0.3 }}
              />
            </Link>
          </MotionDiv>

          {/* Sign In Card */}
          <MotionDiv className="signin-card" variants={itemVariants}>
            <div className="card-header">
              <h1 className="signin-title">Welcome Back</h1>
              <p className="signin-subtitle">Sign in to your POSlah! account</p>
            </div>

            {error && (
              <MotionDiv 
                className="error-message"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <i className="bi bi-exclamation-triangle"></i>
                {error}
              </MotionDiv>
            )}

            <form onSubmit={handleSubmit} className="signin-form">
              <MotionDiv className="form-group" variants={itemVariants}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  inputMode="text"
                  required
                />
                <span className="input-highlight"></span>
              </MotionDiv>

              <MotionDiv className="form-group" variants={itemVariants}>
                <input
                  type="password"
                  className="form-input"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  required
                />
                <span className="input-highlight"></span>
              </MotionDiv>

              <MotionButton
                type="submit"
                className="signin-btn"
                disabled={isLoading}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                variants={itemVariants}
              >
                {isLoading ? (
                  <>
                    <span className="loading-spinner"></span>
                    Signing in...
                  </>
                ) : (
                  "Let's go"
                )}
              </MotionButton>
            </form>
            
            <MotionDiv className="signin-footer" variants={itemVariants}>
              <p>Don't have an account?</p>
              <Link to="/getstarted" className="signup-link">
                Get Started
              </Link>
            </MotionDiv>
          </MotionDiv>
        </MotionDiv>
      </MotionDiv>
    </div>
  );
};

export default SignIn;